<template>
  <div class="content-wrapper view-voucher-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h4 class="mb-0"><i class="fas fa-ticket-alt"></i> จัดการบัตรกำนัล</h4>
            <b-button v-if="canManageVouchers" variant="primary" @click="openCreateForm">
              + สร้างชุดบัตร
            </b-button>
          </div>

          <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

          <b-table
            :items="getBatchList"
            :fields="fields"
            :busy="isLoading"
            responsive
            hover
            show-empty
            empty-text="ยังไม่มีชุดบัตรกำนัล"
          >
            <template #cell(validTo)="{ value }">
              <span v-if="!value">—</span>
              <span v-else>{{ formatValidTo(value) }}</span>
            </template>
            <template #cell(redemptionRate)="{ value }">
              {{ value }}%
            </template>
            <template #cell(assigned)="{ item }">
              <span v-if="!item.assigned" class="text-muted">—</span>
              <span v-else>{{ item.assigned }} / {{ item.issued }}</span>
            </template>
            <template #cell(status)="{ value }">
              <b-badge :variant="statusVariant(value)">{{ statusLabel(value) }}</b-badge>
            </template>
            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end flex-nowrap">
                <VoucherExportButtons :batch="item" />
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="mr-1"
                  @click="openReport(item)"
                >
                  <i class="fas fa-chart-bar mr-1"></i>รายงาน
                </b-button>
                <b-button
                  v-if="canManageVouchers && nextStatus(item.status)"
                  size="sm"
                  variant="outline-primary"
                  @click="changeStatus(item)"
                >
                  <i :class="nextStatusIcon(item.status)" class="mr-1"></i
                  >{{ nextStatusLabel(item.status) }}
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <b-modal ref="batchFormModal" title="สร้างชุดบัตรกำนัล" size="lg" hide-footer>
        <VoucherBatchForm @created="onBatchCreated" @cancel="$refs.batchFormModal.hide()" />
      </b-modal>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import VoucherBatchForm from "@/components/voucher/VoucherBatchForm.vue";
import VoucherExportButtons from "@/components/voucher/VoucherExportButtons.vue";
import { PERMISSIONS } from "@/constants/roles";

export default {
  name: "VoucherManagement",
  components: { VoucherBatchForm, VoucherExportButtons },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      fields: [
        { key: "name", label: "ชื่อแคมเปญ", sortable: true },
        { key: "codePrefix", label: "คำนำหน้ารหัส" },
        { key: "issued", label: "ออก", class: "text-right" },
        { key: "redeemed", label: "ใช้แล้ว", class: "text-right" },
        { key: "remaining", label: "คงเหลือ", class: "text-right" },
        { key: "voided", label: "ยกเลิก", class: "text-right" },
        // How much of the batch is spoken for. Orthogonal to the status columns
        // above — a bound code can be UNUSED, REDEEMED or VOID — so these two
        // sum to `issued`, not to `remaining`.
        { key: "assigned", label: "ผูกคนไข้", class: "text-right" },
        { key: "redemptionRate", label: "อัตราการใช้", class: "text-right" },
        { key: "validTo", label: "หมดอายุ" },
        { key: "status", label: "สถานะ" },
        { key: "actions", label: "", class: "text-right text-nowrap actions-cell" },
      ],
    };
  },
  computed: {
    ...mapGetters("moduleVoucher", ["getBatchList"]),
    ...mapGetters("moduleUser", { can: "can", getBranchInfo: "getBranchInfo" }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    // voucher.view (the gate on this page's list) is wider than voucher.manage.
    // A branch MANAGER/HEADCOUNTER reads the batches but may not mint one or flip
    // its status — the BE answers 403 — so the buttons that would do that are not
    // offered rather than shown and then refused in a red alert.
    canManageVouchers() {
      const clinicId = this.getBranchInfo?.clinic?.id;
      return this.can(PERMISSIONS.VOUCHER_MANAGE, { clinicId });
    },
  },
  methods: {
    ...mapActions("moduleVoucher", ["fetchBatches", "setBatchStatus"]),

    formatValidTo(value) {
      return moment(value).format("DD/MM/YYYY");
    },
    statusLabel(status) {
      return { DRAFT: "ร่าง", ACTIVE: "เปิดใช้", CLOSED: "ปิด" }[status] || status;
    },
    statusVariant(status) {
      return { DRAFT: "secondary", ACTIVE: "success", CLOSED: "dark" }[status] || "light";
    },
    // DRAFT -> ACTIVE -> CLOSED, forward only. The backend rejects anything else,
    // so the UI simply does not offer it.
    nextStatus(status) {
      return { DRAFT: "ACTIVE", ACTIVE: "CLOSED" }[status] || null;
    },
    nextStatusLabel(status) {
      return { DRAFT: "เปิดใช้", ACTIVE: "ปิดชุด" }[status] || "";
    },
    nextStatusIcon(status) {
      return { DRAFT: "fas fa-play-circle", ACTIVE: "fas fa-lock" }[status] || "";
    },

    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        await this.fetchBatches({ clinicUrl: this.clinicUrl });
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "โหลดรายการชุดบัตรไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },

    async changeStatus(batch) {
      const status = this.nextStatus(batch.status);
      if (!status) return;
      const question = status === "ACTIVE"
        ? `เปิดใช้ชุดบัตร "${batch.name}" ใช่หรือไม่? รหัสทั้งหมดจะเริ่มใช้ได้ทันที`
        : `ปิดชุดบัตร "${batch.name}" ใช่หรือไม่? รหัสที่ยังไม่ถูกใช้จะใช้ไม่ได้อีก`;
      const confirmed = await this.$bvModal.msgBoxConfirm(question, {
        title: "ยืนยัน", okTitle: "ตกลง", cancelTitle: "ยกเลิก", centered: true,
      });
      if (!confirmed) return;

      try {
        await this.setBatchStatus({ clinicUrl: this.clinicUrl, batchId: batch.id, status });
        await this.load();
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "เปลี่ยนสถานะไม่สำเร็จ";
      }
    },

    openCreateForm() {
      this.$refs.batchFormModal.show();
    },
    async onBatchCreated() {
      this.$refs.batchFormModal.hide();
      await this.load();
    },
    openReport(batch) {
      this.$router.push({
        name: "VoucherBatchReport",
        params: { ...this.$route.params, batchId: String(batch.id) },
      });
    },
  },
  created() {
    this.load();
  },
};
</script>
